import { Table, TableWeekdayAvailability } from "@/types/table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Users, MoreVertical, Edit2, Calendar, Trash2, CalendarDays, Link2, Copy, Minus, Plus, MapPin } from "lucide-react";
import { getAvailableDaysLabel } from "@/lib/tableAvailabilityUtils";

interface TableCardProps {
  table: Table;
  weekdayAvailability?: TableWeekdayAvailability | null;
  zone?: { id: string; name: string; color?: string | null } | null;
  onToggleActive: (id: string) => void;
  onEdit: (table: Table) => void;
  onManageAvailability: (table: Table) => void;
  onDelete: (table: Table) => void;
  onCombine?: (table: Table) => void;
  onDuplicate?: (table: Table) => void;
  onCapacityChange?: (table: Table, delta: number) => void;
  isHighlighted?: boolean;
}

export function TableCard({ 
  table,
  weekdayAvailability,
  zone,
  onToggleActive, 
  onEdit, 
  onManageAvailability, 
  onDelete,
  onCombine,
  onDuplicate,
  onCapacityChange,
  isHighlighted = false
}: TableCardProps) {
  return (
    <Card className={`hover:shadow-md transition-all ${!table.isActive ? "opacity-60 border-dashed" : ""} ${table.isBookable === false ? "border-amber-300 border-dashed" : ""} ${isHighlighted ? "ring-2 ring-primary shadow-lg" : ""}`}>
      <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
        <div className="space-y-1">
          <CardTitle className="text-lg font-semibold">{table.name}</CardTitle>
          <div className="flex items-center gap-2 flex-wrap">
            <Badge 
              variant={table.isActive ? "default" : "secondary"}
              className={table.isActive ? "bg-green-500" : "bg-gray-400"}
            >
              {table.isActive ? "Activée" : "Désactivée"}
            </Badge>
            {table.isBookable === false && (
              <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-300">
                Hors réservation
              </Badge>
            )}
            {weekdayAvailability && weekdayAvailability.availableDays.length < 7 && (
              <Badge variant="outline" className="text-xs">
                <CalendarDays className="w-3 h-3 mr-1" />
                {getAvailableDaysLabel(weekdayAvailability.availableDays)}
              </Badge>
            )}
            {zone && (
              <Badge variant="outline" className="text-xs gap-1" title="Zone réservable">
                {zone.color && (
                  <span
                    className="inline-block w-2.5 h-2.5 rounded-full border border-border"
                    style={{ backgroundColor: zone.color }}
                    aria-hidden
                  />
                )}
                <MapPin className="w-3 h-3" />
                {zone.name}
              </Badge>
            )}
          </div>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon">
              <MoreVertical className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onEdit(table)}>
              <Edit2 className="w-4 h-4 mr-2" />
              Modifier
            </DropdownMenuItem>
            {onDuplicate && (
              <DropdownMenuItem onClick={() => onDuplicate(table)}>
                <Copy className="w-4 h-4 mr-2" />
                Dupliquer
              </DropdownMenuItem>
            )}
            {onCombine && (
              <DropdownMenuItem onClick={() => onCombine(table)}>
                <Link2 className="w-4 h-4 mr-2" />
                Combinaisons
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={() => onManageAvailability(table)}>
              <Calendar className="w-4 h-4 mr-2" />
              Gérer la disponibilité
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem 
              onClick={() => onDelete(table)}
              className="text-destructive"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Supprimer
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          <div className="flex items-center text-muted-foreground">
            <Users className="w-4 h-4 mr-2 flex-shrink-0" />
            {onCapacityChange ? (
              <div className="flex items-center gap-1.5 flex-1">
                <Button
                  variant="outline"
                  size="icon"
                  className="h-7 w-7"
                  onClick={() => onCapacityChange(table, -1)}
                  disabled={table.capacity <= 1}
                >
                  <Minus className="w-3 h-3" />
                </Button>
                <span className="text-sm font-medium min-w-[3rem] text-center">
                  {table.capacity} places
                </span>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-7 w-7"
                  onClick={() => onCapacityChange(table, 1)}
                >
                  <Plus className="w-3 h-3" />
                </Button>
              </div>
            ) : (
              <span className="text-sm">{table.capacity} places</span>
            )}
          </div>
          {table.notes && (
            <p className="text-sm text-muted-foreground italic">{table.notes}</p>
          )}
          <div className="flex items-center justify-between pt-2 border-t">
            <span className="text-sm text-muted-foreground">
              {table.isActive ? "Activée" : "Désactivée"}
            </span>
            <Switch
              checked={table.isActive}
              onCheckedChange={() => onToggleActive(table.id)}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
